/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { format, parseISO, isValid } from 'date-fns';
import Navbar from '@/components/layout/Navbar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  ArrowLeft,
  Loader2,
  Save,
  PlayCircle,
  AtSign,
  Hash,
  CheckCircle2,
  AlertCircle,
  Clock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const token = () => localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });

const parseSources = (raw) =>
  (raw || '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

const prettyTime = (iso) => {
  if (!iso) return '';
  try {
    const d = parseISO(iso);
    if (!isValid(d)) return iso.slice(0, 16);
    return format(d, 'd MMM yyyy, HH:mm');
  } catch {
    return iso.slice(0, 16);
  }
};

const AdminAutoScrapeSettingsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [sourcesText, setSourcesText] = useState('@igotskates');
  const [postsPerSource, setPostsPerSource] = useState(2);
  const [intervalHours, setIntervalHours] = useState(6);
  const [allowedIntervals, setAllowedIntervals] = useState([1, 3, 6, 12, 24]);
  const [nextRunAt, setNextRunAt] = useState('');
  const [runs, setRuns] = useState([]);

  const parsed = useMemo(() => parseSources(sourcesText), [sourcesText]);
  const handleCount = parsed.filter((s) => !s.startsWith('#')).length;
  const hashtagCount = parsed.filter((s) => s.startsWith('#')).length;

  const load = async () => {
    try {
      const [settingsRes, runsRes] = await Promise.all([
        axios.get(`${API}/admin/instagram/auto-scrape/settings`, { headers: authHeaders() }),
        axios.get(`${API}/admin/instagram/auto-scrape/runs?limit=10`, { headers: authHeaders() }),
      ]);
      const s = settingsRes.data || {};
      setEnabled(!!s.enabled);
      setSourcesText((s.sources || []).join('\n'));
      setPostsPerSource(s.posts_per_source || 2);
      setIntervalHours(s.interval_hours || 6);
      if (Array.isArray(s.allowed_intervals) && s.allowed_intervals.length) {
        setAllowedIntervals(s.allowed_intervals);
      }
      setNextRunAt(s.next_run_at || '');
      setRuns(runsRes.data?.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    const list = parseSources(sourcesText);
    if (list.length === 0) {
      toast.error('Add at least one @handle or #hashtag');
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/instagram/auto-scrape/settings`,
        { enabled, sources: list, posts_per_source: postsPerSource, interval_hours: intervalHours },
        { headers: authHeaders() },
      );
      toast.success('Saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/instagram/auto-scrape/run-now`,
        {},
        { headers: authHeaders() },
      );
      if (data?.skipped) {
        toast.info(`Skipped: ${data.reason}`);
      } else {
        const t = data?.totals || {};
        toast.success(
          `Done — queued ${t.queued || 0}, fetched ${t.fetched || 0}.`,
        );
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24" data-testid="admin-auto-scrape-settings-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 pt-24 pb-12 space-y-6">
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="settings-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-xl font-semibold">Auto-Scrape Settings</h1>
          <div className="w-12" />
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            <Card className="bg-card border-border/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  Nightly Instagram scrape
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Runs <span className="text-foreground font-medium">{nextRunAt || 'every hour at :00 (Europe/London)'}</span>.
                  Configure which handles &amp; hashtags the cron job pulls from each hour.
                </p>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-sm">Enabled</Label>
                    <p className="text-xs text-muted-foreground">Turn the nightly job on or off.</p>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={setEnabled}
                    data-testid="auto-scrape-enabled-switch"
                  />
                </div>

                <div>
                  <Label htmlFor="sources">Sources</Label>
                  <Textarea
                    id="sources"
                    data-testid="auto-scrape-sources-input"
                    value={sourcesText}
                    onChange={(e) => setSourcesText(e.target.value)}
                    placeholder={'@igotskates\n@rollerdiscolondon\n#rollerdisco'}
                    rows={5}
                    className="font-mono text-sm"
                  />
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <AtSign className="w-3 h-3" /> {handleCount} handle{handleCount !== 1 ? 's' : ''}
                    </span>
                    <span className="flex items-center gap-1">
                      <Hash className="w-3 h-3" /> {hashtagCount} hashtag{hashtagCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    One per line, or comma-separated. Use <code className="px-1 bg-muted rounded">@handle</code> for profiles and <code className="px-1 bg-muted rounded">#tag</code> for global hashtag searches.
                  </p>
                </div>

                <div>
                  <Label htmlFor="posts-per-source">Posts to fetch per source</Label>
                  <Input
                    id="posts-per-source"
                    data-testid="auto-scrape-posts-input"
                    type="number"
                    min="1"
                    max="50"
                    value={postsPerSource}
                    onChange={(e) => setPostsPerSource(parseInt(e.target.value, 10) || 2)}
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Each post = an Apify run. Organisers rarely post multiple events at once — <span className="text-emerald-400">2</span> is the recommended default. The next 6h run picks up anything missed.
                  </p>
                </div>

                <div>
                  <Label htmlFor="interval-hours">Scrape cadence</Label>
                  <select
                    id="interval-hours"
                    data-testid="auto-scrape-interval-select"
                    value={intervalHours}
                    onChange={(e) => setIntervalHours(parseInt(e.target.value, 10))}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    {allowedIntervals.map((h) => (
                      <option key={h} value={h}>
                        Every {h} hour{h === 1 ? '' : 's'} ({Math.round(24 / h)} runs/day)
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="text-amber-300">⚠️ Apify is metered.</span> Hourly burned through the $29 monthly cap. <span className="text-emerald-400">Every 6h</span> is the recommended default.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 pt-2">
                  <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
                    {saving ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
                    ) : (
                      <><Save className="w-4 h-4 mr-2" />Save settings</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={runNow}
                    disabled={running}
                    data-testid="run-now-btn"
                  >
                    {running ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Running…</>
                    ) : (
                      <><PlayCircle className="w-4 h-4 mr-2" />Run now</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Recent runs</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Last 10 nightly &amp; manual runs.
                </p>
              </CardHeader>
              <CardContent>
                {runs.length === 0 ? (
                  <p className="text-sm text-muted-foreground py-6 text-center">
                    No runs yet — tap <span className="text-foreground">Run now</span> above to start one.
                  </p>
                ) : (
                  <ul className="divide-y divide-border/30" data-testid="recent-runs-list">
                    {runs.map((r, i) => {
                      const t = r.totals || {};
                      const hasError = (r.per_source || []).some((s) => s.error);
                      return (
                        <li key={i} className="py-3 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-sm flex items-center gap-2">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                              <span className="truncate">{prettyTime(r.started_at)}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {(r.per_source || []).map((p) => p.source || p.username || '?').join(' · ') || '—'}
                            </div>
                            <div className="text-[11px] text-muted-foreground/80 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                              <span>fetched <span className="text-foreground">{t.fetched || 0}</span></span>
                              <span>already seen <span className="text-foreground">{t.skipped_already_seen || 0}</span></span>
                              <span>off-topic <span className="text-foreground">{t.skipped_off_topic || 0}</span></span>
                              <span>past <span className="text-foreground">{t.skipped_past || 0}</span></span>
                              <span>unreadable <span className="text-foreground">{t.skipped_unreadable || 0}</span></span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <Badge variant="outline" className="text-emerald-300 border-emerald-800">
                              {t.queued || 0} queued
                            </Badge>
                            {hasError ? (
                              <Badge variant="outline" className="text-red-300 border-red-800">
                                <AlertCircle className="w-3 h-3 mr-1" />error
                              </Badge>
                            ) : (
                              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminAutoScrapeSettingsPage;
